import { useState } from "react";
import {
  addUser as addUserService,
  type AddUserParameters,
  signupUserWithEmail,
} from "@/services/auth";
import { FirebaseError } from "firebase/app";
import { t } from "i18next";
import { AUTH_ERRORS } from "@/common/errors";

type CreateUserParameters = Omit<AddUserParameters, "fUid"> & {
  password: string;
};

interface UseCreateUserState {
  createUser: (body: CreateUserParameters) => Promise<void>;
  result: boolean;
  loading: boolean;
  error: string | null;
}

export const useCreateUser = (): UseCreateUserState => {
  const [result, setResult] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const createUser = async (body: CreateUserParameters): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const userCredential = await signupUserWithEmail({
        email: body.email,
        password: body.password,
      });
      const fUid = userCredential.user.uid;
      await addUserService({
        email: body.email,
        firstname: body.firstname,
        lastname: body.lastname,
        fUid: fUid,
        occupationId: body.occupationId,
      });
      setResult(true);
    } catch (error) {
      if (
        error instanceof FirebaseError &&
        error.code == AUTH_ERRORS.EMAIL_ALREADY_IN_USE
      ) {
        setError(t("signUp.form.messages.emailUsed"));
      } else {
        setError(t("signUp.form.messages.error"));
      }
      setResult(false);
    } finally {
      setLoading(false);
    }
  };
  return { createUser, result, loading, error };
};
